import React, { useEffect, useState } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';

/**
 * LanguageSwitcher — uses Google Translate widget under the hood.
 *
 * Why Google Translate?
 *  - 100+ languages covered (incl. all 11 major Indian + Urdu)
 *  - Auto-translates DYNAMIC content (news headlines, market data, AI chat replies, blog posts)
 *  - No API key, no per-call cost, no maintenance
 *  - Loads only when first opened (lazy) — zero startup cost
 *
 * Languages selected for India: English (default) + 10 Indian languages.
 */

const LANGUAGES = [
  { code: 'en',     label: 'English',        native: 'English',     flag: '🇬🇧' },
  { code: 'hi',     label: 'Hindi',          native: 'हिन्दी',         flag: '🇮🇳' },
  { code: 'bn',     label: 'Bengali',        native: 'বাংলা',        flag: '🇧🇩' },
  { code: 'ta',     label: 'Tamil',          native: 'தமிழ்',        flag: '🇮🇳' },
  { code: 'te',     label: 'Telugu',         native: 'తెలుగు',       flag: '🇮🇳' },
  { code: 'mr',     label: 'Marathi',        native: 'मराठी',        flag: '🇮🇳' },
  { code: 'gu',     label: 'Gujarati',       native: 'ગુજરાતી',      flag: '🇮🇳' },
  { code: 'kn',     label: 'Kannada',        native: 'ಕನ್ನಡ',         flag: '🇮🇳' },
  { code: 'ml',     label: 'Malayalam',      native: 'മലയാളം',      flag: '🇮🇳' },
  { code: 'pa',     label: 'Punjabi',        native: 'ਪੰਜਾਬੀ',         flag: '🇮🇳' },
  { code: 'ur',     label: 'Urdu',           native: 'اردو',         flag: '🇵🇰' },
  { code: 'or',     label: 'Odia',           native: 'ଓଡ଼ିଆ',         flag: '🇮🇳' },
  { code: 'as',     label: 'Assamese',       native: 'অসমীয়া',       flag: '🇮🇳' },
  // Global popular
  { code: 'ar',     label: 'Arabic',         native: 'العربية',      flag: '🇸🇦' },
  { code: 'es',     label: 'Spanish',        native: 'Español',     flag: '🇪🇸' },
  { code: 'fr',     label: 'French',         native: 'Français',    flag: '🇫🇷' },
  { code: 'de',     label: 'German',         native: 'Deutsch',     flag: '🇩🇪' },
  { code: 'zh-CN',  label: 'Chinese',        native: '中文',          flag: '🇨🇳' },
];

const LS_KEY = 'arthik_lang_v2';

// Set Google Translate cookie + select dropdown
function applyLanguage(code) {
  if (typeof document === 'undefined') return;
  // Persist user preference
  try { localStorage.setItem(LS_KEY, code); } catch { /* ignore */ }
  // Google Translate uses a cookie + the hidden <select> element
  const value = code === 'en' ? '/auto/en' : `/auto/${code}`;
  const expires = new Date(Date.now() + 365 * 24 * 3600 * 1000).toUTCString();
  // Set on both root domain and host (Google looks at both)
  document.cookie = `googtrans=${value}; expires=${expires}; path=/`;
  try {
    const host = window.location.hostname;
    const parts = host.split('.');
    if (parts.length >= 2) {
      const root = parts.slice(-2).join('.');
      document.cookie = `googtrans=${value}; expires=${expires}; path=/; domain=.${root}`;
    }
  } catch { /* ignore */ }
  // Apply via the hidden select if widget is already mounted
  const sel = document.querySelector('.goog-te-combo');
  if (sel) {
    sel.value = code;
    sel.dispatchEvent(new Event('change'));
  } else {
    // Widget will pick up the cookie on next render — force a reload to apply now
    if (code !== 'en') {
      window.location.reload();
    } else {
      // For English, just remove translation by reloading without cookie effect (cookie set to /auto/en is OK)
      window.location.reload();
    }
  }
}

function loadGoogleTranslate() {
  if (typeof window === 'undefined' || window.__gt_loaded__) return;
  window.__gt_loaded__ = true;

  // Init callback Google looks for
  window.googleTranslateElementInit = function () {
    /* global google */
    new window.google.translate.TranslateElement({
      pageLanguage: 'en',
      includedLanguages: LANGUAGES.map((l) => l.code).join(','),
      autoDisplay: false,
      layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
    }, 'arthik-google-translate');
  };

  // Load library
  const s = document.createElement('script');
  s.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  s.async = true;
  document.body.appendChild(s);

  // Hide Google's default UI bar that pushes content down
  const style = document.createElement('style');
  style.innerHTML = `
    .goog-te-banner-frame, .goog-te-banner-frame.skiptranslate { display: none !important; }
    body { top: 0 !important; position: static !important; }
    .goog-tooltip, .goog-tooltip:hover { display: none !important; }
    .goog-text-highlight { background-color: transparent !important; box-shadow: none !important; }
    #arthik-google-translate { display: none; }
  `;
  document.head.appendChild(style);
}

const LanguageSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('en');

  useEffect(() => {
    loadGoogleTranslate();
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) setActive(saved);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest?.('[data-language-switcher]')) setOpen(false);
    };
    if (open) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  const handlePick = (code) => {
    setActive(code);
    setOpen(false);
    applyLanguage(code);
  };

  const current = LANGUAGES.find((l) => l.code === active) || LANGUAGES[0];

  return (
    <div data-language-switcher style={{ position: 'relative' }} data-testid="language-switcher">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((x) => !x); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-switcher-trigger"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '0.45rem 0.7rem',
          background: 'white',
          border: '1px solid #E2E8F0',
          borderRadius: 8,
          color: '#475569',
          fontSize: '0.78rem',
          fontWeight: 700,
          cursor: 'pointer',
          transition: 'all .15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FF6B35'; e.currentTarget.style.color = '#FF6B35'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = '#475569'; }}
      >
        <Globe size={14} />
        <span style={{ fontSize: '1rem' }}>{current.flag}</span>
        <span>{current.native}</span>
        <ChevronDown size={12} style={{ transition: 'transform .15s ease', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="language-switcher-menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 1500,
            background: 'white',
            borderRadius: 12,
            padding: '0.5rem',
            boxShadow: '0 20px 50px rgba(0,0,0,0.12), 0 0 0 1px #E2E8F0',
            width: 240,
            maxHeight: 360,
            overflowY: 'auto',
            animation: 'lang-in .15s ease',
          }}
        >
          <div style={{ padding: '4px 10px 6px', fontSize: '0.7rem', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Select Language
          </div>
          {LANGUAGES.map((l) => {
            const selected = l.code === active;
            return (
              <button
                key={l.code}
                role="option"
                aria-selected={selected}
                onClick={() => handlePick(l.code)}
                data-testid={`lang-option-${l.code}`}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: selected ? '#FFF7ED' : 'white',
                  color: '#0F172A',
                  border: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '0.88rem',
                  fontWeight: selected ? 700 : 500,
                  transition: 'background .12s ease',
                }}
                onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = '#F8FAFC'; }}
                onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'white'; }}
              >
                <span style={{ fontSize: '1.05rem' }}>{l.flag}</span>
                <span style={{ flex: 1 }}>{l.native}</span>
                <span style={{ fontSize: '0.7rem', color: '#94A3B8', fontWeight: 500 }}>{l.label}</span>
                {selected && <Check size={14} style={{ color: '#FF6B35' }} />}
              </button>
            );
          })}
        </div>
      )}

      {/* Hidden Google Translate mount point */}
      <div id="arthik-google-translate" />

      <style>{`@keyframes lang-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};

export default LanguageSwitcher;
